import type { SheetRow } from '@/lib/sheet'

/* ==================================================================== */
/*  Cosmos content that the client maintains in a Google Sheet.          */
/*  This file defines the fixed structures (zodiac signs, Muhurat         */
/*  categories) and the fallback DEMO rows shown until the real sheet is  */
/*  connected. The column names below are exactly the header row the      */
/*  client should use in each sheet tab.                                  */
/* ==================================================================== */

/* ---- Zodiac signs (fixed) --------------------------------------------- */
export const zodiacSigns = [
  { name: 'Aries', symbol: '♈', dates: 'Mar 21 – Apr 19' },
  { name: 'Taurus', symbol: '♉', dates: 'Apr 20 – May 20' },
  { name: 'Gemini', symbol: '♊', dates: 'May 21 – Jun 20' },
  { name: 'Cancer', symbol: '♋', dates: 'Jun 21 – Jul 22' },
  { name: 'Leo', symbol: '♌', dates: 'Jul 23 – Aug 22' },
  { name: 'Virgo', symbol: '♍', dates: 'Aug 23 – Sep 22' },
  { name: 'Libra', symbol: '♎', dates: 'Sep 23 – Oct 22' },
  { name: 'Scorpio', symbol: '♏', dates: 'Oct 23 – Nov 21' },
  { name: 'Sagittarius', symbol: '♐', dates: 'Nov 22 – Dec 21' },
  { name: 'Capricorn', symbol: '♑', dates: 'Dec 22 – Jan 19' },
  { name: 'Aquarius', symbol: '♒', dates: 'Jan 20 – Feb 18' },
  { name: 'Pisces', symbol: '♓', dates: 'Feb 19 – Mar 20' },
] as const

/* ---- Muhurat categories (fixed) --------------------------------------- */
export const muhuratCategories = ['Car', 'House', 'Marriage'] as const
export type MuhuratCategory = (typeof muhuratCategories)[number]

/* ==================================================================== */
/*  SHEET TAB: "DailyHoroscope"                                           */
/*  Columns:  sign | date | text                                         */
/*    sign  — one of the 12 sign names above                             */
/*    date  — YYYY-MM-DD (the day this reading is for)                   */
/*    text  — the horoscope copy                                         */
/*  Fill a month at a time; the site shows today's row per sign          */
/*  automatically. If no row matches today, the newest past row is used. */
/* ==================================================================== */
export const demoDailyHoroscope: SheetRow[] = zodiacSigns.map((z) => ({
  sign: z.name,
  date: '',
  text: `Demo reading for ${z.name}: a day for quiet intuition and small, kind repairs. Say the soft thing first. Full readings for your sign go live once the community launches.`,
}))

/* ==================================================================== */
/*  SHEET TAB: "Yearly2026"                                              */
/*  Columns:  sign | text                                                */
/*    One row per sign; written once for the year, edited any time.      */
/* ==================================================================== */
export const demoYearly2026: SheetRow[] = zodiacSigns.map((z) => ({
  sign: z.name,
  text: `Demo 2026 outlook for ${z.name}: a year that rewards patience over speed. Career steadies by spring, home softens by autumn, and the heart asks for honesty. The full sign-by-sign edition lives inside Cosmos.`,
}))

/* ==================================================================== */
/*  SHEET TAB: "Muhurat"                                                 */
/*  Columns:  category | date | day | time | note                       */
/*    category — Car | House | Marriage                                 */
/*    date     — e.g. 12 August 2026 (match the zodiac sheet's format)   */
/*    day      — e.g. Tuesday                                            */
/*    time     — e.g. 10:42 AM – 12:08 PM                               */
/*    note     — short guidance (optional)                              */
/* ==================================================================== */
/* A row with a blank `time` is a guidance note, not a bookable window:
   a blank `date` makes it a category-wide note, a month makes it month-scoped.
   House and Marriage stay empty until November (Chaturmas), which the UI
   explains on its own. */
export const demoMuhurat: SheetRow[] = [
  { category: 'Car', date: '24 August 2026', day: 'Monday', time: '08:20 PM – 06:03 AM', note: 'Uttara Ashadha, window ends 25 August.' },
  { category: 'Car', date: '13 September 2026', day: 'Sunday', time: '06:04 AM – 07:06 AM', note: 'Hasta, window ends 14 September.' },
  { category: 'House', date: '', day: '', time: '', note: 'Some Grah Pravesh muhurats continue past midnight; the next-day date is shown in brackets.' },
  { category: 'House', date: 'November 2026', day: '', time: '', note: 'Auspicious timings follow Devuthani Ekadashi.' },
  { category: 'House', date: '11 November 2026', day: 'Wednesday', time: '06:40 AM – 11:38 AM', note: '' },
  { category: 'House', date: '1 December 2026', day: 'Tuesday', time: '07:12 AM – 09:40 AM', note: '' },
  { category: 'Marriage', date: '', day: '', time: '', note: 'These are general marriage muhurats. For timings matched to your own stars, a personalised consultation with one of our astrologers is recommended.' },
  { category: 'Marriage', date: 'November 2026', day: '', time: '', note: 'Marriages in November begin after Devuthani Ekadashi.' },
  { category: 'Marriage', date: '21 November 2026', day: 'Saturday', time: 'Auspicious all day', note: '' },
  { category: 'Marriage', date: '2 December 2026', day: 'Wednesday', time: 'Auspicious all day', note: '' },
]
